import { useState } from 'react'
import { useAuth } from '@/lib/auth'
import { takeIdleNotice } from '@/lib/idle'
import { ErrorNote } from '@/components/ui'
import { AwLogo } from '@/components/AwLogo'
import mark from '@/assets/taskmanager-mark.png'

/** Email and password, with an emailed one-time code to set or reset it.
 *
 *  The same code flow covers first-time setup and a forgotten password, so someone
 *  just added to the Users tab needs no separate invitation step.
 */
type Mode = 'password' | 'request' | 'code'

// `border-rule bg-surface text-ink`, not the literal #D5D3CD on white it used to be.
// Those are the DEFAULT theme's values, so on either dark theme the sign-in page -
// the first screen anybody sees, and the only one a locked-out person sees at all -
// showed two glaring white slabs on a charcoal card. `placeholder:text-ink-3` comes
// with it, or the hint text keeps its browser default and vanishes on the dark ground.
const FIELD =
  'w-full h-11 px-3 rounded-md border border-rule bg-surface text-ink ' +
  'placeholder:text-ink-3 text-base sm:text-[14.5px] ' +
  'focus:outline-none focus:border-primary'
const BUTTON =
  'w-full h-11 rounded-md bg-primary text-primary-on font-medium text-[14.5px] ' +
  'hover:opacity-95 disabled:opacity-60'

/** A password box you can read back.
 *
 *  Typing a password blind is where a good share of failed sign-ins come from, and
 *  the cost of one here is real: five failures locks the account for fifteen minutes.
 *  On a phone, where this app is mostly used, it is worse - autocorrect and a small
 *  keyboard, with no way to check what actually went in.
 *
 *  `type="button"` is load-bearing: inside a form a bare button submits it, so the
 *  toggle would try to sign in on every tap. Words rather than an eye glyph, because
 *  the eye is ambiguous about which state it is offering, and this file already
 *  styles its secondary actions as text links.
 */
function PasswordField({ value, onChange, placeholder, autoComplete }: {
  value: string
  onChange: (v: string) => void
  placeholder: string
  autoComplete: 'current-password' | 'new-password'
}) {
  const [shown, setShown] = useState(false)
  return (
    <div className="relative">
      {/* Room on the right for the toggle, so a long password does not run under it. */}
      <input className={FIELD + ' pr-[4.5rem]'} type={shown ? 'text' : 'password'}
             autoComplete={autoComplete} required placeholder={placeholder}
             value={value} onChange={(e) => onChange(e.target.value)} />
      <button type="button" onClick={() => setShown((s) => !s)}
              aria-pressed={shown}
              aria-label={shown ? 'Hide password' : 'Show password'}
              className="absolute right-0 top-0 h-11 px-3 text-[12.5px] font-medium
                         text-primary hover:underline">
        {shown ? 'Hide' : 'Show'}
      </button>
    </div>
  )
}

export function SignIn() {
  const { login, requestCode, setPassword } = useAuth()
  const [mode, setMode] = useState<Mode>('password')
  const [email, setEmail] = useState('')
  const [password, setPw] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNew] = useState('')
  const [err, setErr] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  // Why they are looking at this screen, when they were signed in yesterday. Read
  // once, in the initialiser: `takeIdleNotice` consumes the note, so calling it on
  // every render would clear it before the first paint - and it must not reappear
  // on the next visit either.
  const [note, setNote] = useState(
    () => (takeIdleNotice()
      ? 'You were signed out after 24 hours without activity. Sign in to carry on.'
      : ''))

  const run = async (fn: () => Promise<void>) => {
    setErr(null)
    setBusy(true)
    try { await fn() } catch (e) { setErr(e) } finally { setBusy(false) }
  }

  const submitPassword = (e: React.FormEvent) => {
    e.preventDefault()
    run(() => login(email.trim(), password))
  }

  const submitRequest = (e: React.FormEvent) => {
    e.preventDefault()
    run(async () => {
      await requestCode(email.trim())
      // Says "if" on purpose: the API answers identically for an address that
      // cannot sign in, and the UI must not leak what the API withholds.
      setNote('If that address can sign in, a code is on its way. It expires in 10 minutes.')
      setMode('code')
    })
  }

  const submitCode = (e: React.FormEvent) => {
    e.preventDefault()
    run(() => setPassword(email.trim(), code.trim(), newPassword))
  }

  return (
    <div className="min-h-screen grid place-items-center bg-paper p-4 sm:p-6">
      {/* The grid centres a single child, so the card and the credit below it are
          wrapped together - two siblings would land in the same cell and overlap. */}
      <div className="w-full max-w-[404px] flex flex-col items-center gap-5">
      {/* 404px inside a p-6 wrapper needs 452px, so this was the one page that
          actually scrolled sideways on a phone rather than clipping. */}
      <div className="w-full bg-surface border border-rule rounded-xl
                      p-6 sm:p-9 pb-7 shadow-sm">
        {/* The mark above the name. It carries no wordmark of its own, so the name
            stays live text - sharper than an image, and it is the heading the
            document outline needs. */}
        <img src={mark} alt="" aria-hidden width={72} className="mb-3" />
        <h2 className="text-[23px] font-semibold tracking-tight mb-1.5">Task Manager</h2>
        <p className="text-[13.5px] text-ink-2 leading-relaxed">
          Today's list, the approval queue, and every task's history — with a
          complete record of who did what, and when.
        </p>

        {err ? <div className="mt-5"><ErrorNote error={err} /></div> : null}
        {note ? (
          <p className="mt-4 text-[12.2px] text-ink-2 bg-paper border border-rule rounded-md px-3.5 py-3">
            {note}
          </p>
        ) : null}

        {mode === 'password' && (
          <form onSubmit={submitPassword} className="mt-6 space-y-3">
            <input className={FIELD} type="email" autoComplete="username" required
                   placeholder="you@yourfirm.com" value={email}
                   onChange={e => setEmail(e.target.value)} />
            <PasswordField value={password} onChange={setPw}
                           placeholder="Password"
                           autoComplete="current-password" />
            <button className={BUTTON} disabled={busy}>
              {busy ? 'Signing you in…' : 'Sign in'}
            </button>
            <button type="button" onClick={() => { setMode('request'); setNote(''); setErr(null) }}
                    className="w-full text-[12.5px] text-primary hover:underline pt-1">
              Forgot password, or setting one for the first time?
            </button>
          </form>
        )}

        {mode === 'request' && (
          <form onSubmit={submitRequest} className="mt-6 space-y-3">
            <p className="text-[12.5px] text-ink-2">
              We'll email you a 6-digit code.
            </p>
            <input className={FIELD} type="email" autoComplete="username" required
                   placeholder="you@yourfirm.com" value={email}
                   onChange={e => setEmail(e.target.value)} />
            <button className={BUTTON} disabled={busy}>
              {busy ? 'Sending…' : 'Send code'}
            </button>
            <button type="button" onClick={() => { setMode('password'); setNote(''); setErr(null) }}
                    className="w-full text-[12.5px] text-primary hover:underline pt-1">
              Back to sign in
            </button>
          </form>
        )}

        {mode === 'code' && (
          <form onSubmit={submitCode} className="mt-6 space-y-3">
            {/*  The email, read-only, and it has to be here.
              *
              *  This form sets a password, and it had no username field at all - just
              *  the code and the new password. A password manager saving the result
              *  has to name the account something, so it falls back to the nearest
              *  text input, which was the code. The result was a saved login for this
              *  site whose username is a spent six-digit number, offered back on the
              *  sign-in screen ever after. `autocomplete="one-time-code"` does not
              *  prevent it: it says what the field is for, not that it is not the
              *  username.
              *
              *  Naming the account explicitly is the fix. Read-only and out of the tab
              *  order because it is not something to fill in - they typed it on the
              *  previous step - but a real field, because a hidden one is exactly what
              *  managers are entitled to ignore.
              *
              *  It reads better too: the one thing worth confirming before setting a
              *  password is which account is getting it.
              */}
            <label className="block">
              <span className="text-[11.5px] text-ink-3">Setting a password for</span>
              <input className={FIELD + ' bg-paper text-ink-2 mt-1'} type="email"
                     autoComplete="username" value={email} readOnly tabIndex={-1} />
            </label>
            <input className={FIELD} inputMode="numeric" autoComplete="one-time-code"
                   required placeholder="6-digit code" value={code}
                   onChange={e => setCode(e.target.value)} />
            <PasswordField value={newPassword} onChange={setNew}
                           placeholder="New password (at least 8 characters)"
                           autoComplete="new-password" />
            <button className={BUTTON} disabled={busy}>
              {busy ? 'Saving…' : 'Set password and sign in'}
            </button>
            <button type="button" onClick={() => { setMode('request'); setNote(''); setErr(null) }}
                    className="w-full text-[12.5px] text-primary hover:underline pt-1">
              Send another code
            </button>
          </form>
        )}

        {/* Static, and identical for everybody — so it explains where to look without
            revealing whether any given address has an account. Saying "sent to your
            Telegram" in the response would leak both. */}
        <div className="mt-5 bg-paper border border-rule rounded-md px-3.5 py-3 text-[12.2px] text-ink-2 leading-relaxed">
          <b className="text-ink">Where the code goes.</b> To WhatsApp or Telegram if
          you are set up there, otherwise to your email — check spam. It expires in
          10 minutes.
        </div>
      </div>

      {/* Below the card rather than above the heading. A mark above the title reads as
          the product's own logo; this is a credit to whoever built it, and it belongs
          outside the thing it is crediting. */}
      <div className="text-center text-ink-3">
        <p className="text-[11.5px] mb-1.5">Powered by</p>
        <AwLogo height={34} className="text-ink-2 mx-auto max-w-full" />
      </div>
      </div>
    </div>
  )
}
